import fileDownload from 'js-file-download';
import React, { useState, useEffect } from 'react';

import { useTheme } from '@mui/material/styles';
import { Box, Paper, Checkbox, FormGroup, FormControlLabel } from '@mui/material';

import { Containers } from 'src/@types/fileView/enums/fileExtensions';

import { useSelector } from '../../redux/store';
import useLocales from '../../hooks/useLocales';
import useResponsive from '../../hooks/useResponsive';
import useNotifications from '../../hooks/useNotifications';
import { bulkDownload } from '../../services/dlxDriveService';
import { setCheckedItems } from '../../redux/slices/document';
import FolderArchiveDialog from './dialogs/FolderArchiveDialog';
import FolderBulkSignDialog from './dialogs/FolderBulkSignDialog';
import FolderBulkMoveDialog from './dialogs/FolderBulkMoveDialog';
import { addStopLoadingState } from '../../redux/slices/dlxDrive';
import { ModuleTypes } from '../../@types/module/enums/moduleTypes';
import FolderBulkShareDialog from './dialogs/FolderBulkShareDialog';
import ModuleCheck from '../../components/access-checks/ModuleCheck';
import FolderBulkDeleteDialog from './dialogs/FolderBulkDeleteDialog';
import FolderBulkApproveDialog from './dialogs/FolderBulkApproveDialog';
import { FolderActions } from '../../@types/dlx-drive/enums/FolderActions';
import { DLXDriveBulkDownloadModelType } from '../../@types/dlx-drive/types';
import { DocumentStatuses } from '../../@types/documents/enums/documentStatuses';
import DLXDriveBulkActionComponentIconButton from './DLXDriveBulkActionComponentIconButton';

// ----------------------------------------------------------------

const DLXDriveBulkActionComponent: React.FC = () => {
  const { t } = useLocales();
  const { checkedItems } = useSelector((state) => state.document);
  const [checked, setChecked] = useState(false);
  const [isBulkSignDialogOpen, setIsBulkSignDialogOpen] = useState(false);
  const [isBulkApproveDialogOpen, setIsBulkApproveDialogOpen] = useState(false);
  const [isBulkShareDialogOpen, setIsBulkShareDialogOpen] = useState(false);
  const [isBulkDeleteDialogOpen, setIsBulkDeleteDialogOpen] = useState(false);
  const [isBulkMoveDialogOpen, setIsBulkMoveDialogOpen] = useState(false);
  const [isBulkDownloadDisabled, setIsBulkDownloadDisabled] = useState<boolean>(false);
  const [isBulkShareDisabled, setIsBulkShareDisabled] = useState<boolean>(false);
  const [isBulkMoveDisabled, setIsBulkMoveDisabled] = useState<boolean>(false);
  const [isBulkDeleteDisabled, setIsBulkDeleteDisabled] = useState<boolean>(false);
  const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false);

  const theme = useTheme();
  const { enqueueSuccess, enqueueError } = useNotifications();

  const isMobile = useResponsive('only', 'xs');
  const isLight = theme.palette.mode === 'light';

  const handleDownloadClick = async () => {
    const model: DLXDriveBulkDownloadModelType = {
      folderIds: checkedItems.filter((item) => item.isFolder).map((item) => item.id),
      documentIds: checkedItems.filter((item) => !item.isFolder).map((item) => item.id),
    };

    try {
      const file = await bulkDownload(model);

      let { fileName } = file;

      const isContainer = Containers.some((item) => checkedItems[0].name.includes(item));

      if (checkedItems.length === 1 && isContainer) {
        fileName = checkedItems[0].name;
      }

      fileDownload(file.file, fileName, file.mime);
      setCheckedItems([]);
      enqueueSuccess('dlxDrive.notifications.downloadSuccess');
    } catch (error) {
      enqueueError('dlxDrive.errors.downloadError');
    } finally {
      addStopLoadingState();
    }
  };

  const handleUncheckItemsClick = () => {
    setCheckedItems([]);
  };

  const checkBulkDownloadRights = () => {
    setIsBulkDownloadDisabled(
      checkedItems.some(
        (item) =>
          (item.isFolder && item.actions.indexOf(FolderActions.FolderDownload) === -1) ||
          (!item.isFolder && item.actions.indexOf(FolderActions.FileShare) === -1)
      )
    );
  };

  const checkBulkShareRights = () => {
    setIsBulkShareDisabled(
      checkedItems.some(
        (item) =>
          (item.isFolder && item.actions.indexOf(FolderActions.FolderShare) === -1) ||
          (!item.isFolder && item.actions.indexOf(FolderActions.FileShare) === -1)
      )
    );
  };

  const checkBulkMoveRights = () => {
    setIsBulkMoveDisabled(
      checkedItems.some(
        (item) =>
          (item.isFolder && item.actions.indexOf(FolderActions.FolderMove) === -1) ||
          (!item.isFolder && item.actions.indexOf(FolderActions.FileMove) === -1)
      )
    );
  };

  const checkBulkDeleteRights = () => {
    setIsBulkDeleteDisabled(
      checkedItems.some(
        (item) =>
          (item.isFolder && item.actions.indexOf(FolderActions.Delete) === -1) ||
          (!item.isFolder && item.actions.indexOf(FolderActions.FileDelete) === -1)
      )
    );
  };

  useEffect(() => {
    if (checkedItems.length > 0) {
      setChecked(true);
      checkBulkDownloadRights();
      checkBulkShareRights();
      checkBulkMoveRights();
      checkBulkDeleteRights();
    } else {
      setChecked(false);
    }
  }, [checkedItems]);

  return (
    <>
      <Paper
        sx={[
          {
            width: '100%',
            maxWidth: '800px',
            margin: 'auto',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            borderRadius: '16px',
          },
          isMobile
            ? {
                py: 0.5,
              }
            : {
                py: 1,
              },
          isMobile
            ? {
                px: 1,
              }
            : {
                px: 2,
              },
          isLight
            ? {
                backgroundColor: theme.palette.grey[800],
              }
            : {
                backgroundColor: theme.palette.grey[50],
              },
        ]}
        elevation={3}
      >
        <Box>
          <FormGroup>
            <FormControlLabel
              sx={[
                {
                  flexWrap: 'nowrap',
                },
                isLight
                  ? {
                      color: theme.palette.grey[50],
                    }
                  : {
                      color: theme.palette.grey[800],
                    },
              ]}
              control={<Checkbox checked={checked} onClick={handleUncheckItemsClick} id="wpITci" />}
              label={`${checkedItems.length} ${isMobile ? '' : t('dlxDrive.bulkActions.selected')}`}
            />
          </FormGroup>
        </Box>

        <Box
          sx={[
            {
              display: 'flex',
              justifyContent: 'right',
              alignItems: 'center',
            },
            isMobile
              ? {
                  gap: 0.5,
                }
              : {
                  gap: 3,
                },
          ]}
        >
          <DLXDriveBulkActionComponentIconButton
            isDisabled={isBulkDownloadDisabled}
            icon="archive"
            tooltipTitle={t('dlxDrive.bulkActions.archive')}
            onClick={() => setIsArchiveDialogOpen(true)}
            id="n55gZy"
          />

          <DLXDriveBulkActionComponentIconButton
            isDisabled={isBulkDownloadDisabled}
            icon="download"
            tooltipTitle={t('dlxDrive.bulkActions.download')}
            onClick={handleDownloadClick}
            id="nSxgZy"
          />

          <ModuleCheck module={ModuleTypes.EparakstsBulkSignModule}>
            <DLXDriveBulkActionComponentIconButton
              isDisabled={
                checkedItems.filter(
                  (item) => item.status && item.status === DocumentStatuses.WaitingForMe
                ).length === 0
              }
              icon="signature"
              tooltipTitle={t('dlxDrive.bulkActions.sign')}
              onClick={() => setIsBulkSignDialogOpen(true)}
              id="ASKl8Q"
            />
          </ModuleCheck>

          <ModuleCheck module={ModuleTypes.MassApprovalModule}>
            <DLXDriveBulkActionComponentIconButton
              isDisabled={
                checkedItems.filter(
                  (item) => item.status && item.status === DocumentStatuses.WaitingForMeApprove
                ).length === 0
              }
              icon="approve"
              tooltipTitle={t('dlxDrive.bulkActions.approve')}
              onClick={() => setIsBulkApproveDialogOpen(true)}
              id="ASKl6Q"
            />
          </ModuleCheck>

          <DLXDriveBulkActionComponentIconButton
            isDisabled={isBulkShareDisabled}
            icon="share"
            tooltipTitle={t('dlxDrive.bulkActions.share')}
            onClick={() => setIsBulkShareDialogOpen(true)}
            id="YxcFwP"
          />

          <DLXDriveBulkActionComponentIconButton
            isDisabled={isBulkMoveDisabled}
            icon="move"
            tooltipTitle={t('dlxDrive.bulkActions.move')}
            onClick={() => setIsBulkMoveDialogOpen(true)}
            id="YsUVrU"
          />

          <DLXDriveBulkActionComponentIconButton
            isDisabled={isBulkDeleteDisabled}
            icon="delete"
            tooltipTitle={t('dlxDrive.bulkActions.delete')}
            onClick={() => setIsBulkDeleteDialogOpen(true)}
            id="2qr0wv"
          />
        </Box>
      </Paper>

      <FolderBulkSignDialog
        isBulkSignDialogOpen={isBulkSignDialogOpen}
        setIsBulkSignDialogOpen={setIsBulkSignDialogOpen}
      />
      <FolderBulkApproveDialog
        isBulkApproveDialogOpen={isBulkApproveDialogOpen}
        setIsBulkApproveDialogOpen={setIsBulkApproveDialogOpen}
      />
      <FolderBulkShareDialog
        isBulkShareDialogOpen={isBulkShareDialogOpen}
        setIsBulkShareDialogOpen={setIsBulkShareDialogOpen}
      />
      <FolderBulkDeleteDialog
        isBulkDeleteDialogOpen={isBulkDeleteDialogOpen}
        setIsBulkDeleteDialogOpen={setIsBulkDeleteDialogOpen}
      />
      <FolderBulkMoveDialog
        isBulkMoveDialogOpen={isBulkMoveDialogOpen}
        setIsBulkMoveDialogOpen={setIsBulkMoveDialogOpen}
      />
      <FolderArchiveDialog
        isArchiveDialogOpen={isArchiveDialogOpen}
        setIsArchiveDialogOpen={setIsArchiveDialogOpen}
      />
    </>
  );
};

export default DLXDriveBulkActionComponent;
